import { findMenuItemById, hasMenuChildren, isMenuItemForbidden } from './menuNavigationUseCase';
import { filterMenuItemsBySearch } from './menuUseCase';

/**
 * @typedef {'checked' | 'unchecked' | 'indeterminate'} MenuItemCheckState
 */

/**
 * @param {ApiManagerMenuItem} item
 * @param {Partial<ApiManagerMenuItemConfig>} patch
 * @returns {ApiManagerMenuItem}
 */
const patchConfig = (item, patch) => ({ ...item, config: { ...item.config, ...patch } });

/**
 * Set availability on an item and all its descendants. Unavailable items lose their favorite.
 * @param {ApiManagerMenuItem} item
 * @param {boolean} available
 * @returns {ApiManagerMenuItem}
 */
const setSubtreeAvailable = (item, available) => ({
  ...patchConfig(item, available ? { available } : { available, favorite: false }),
  items: item.items ? item.items.map((child) => setSubtreeAvailable(child, available)) : item.items,
});

/**
 * Check/uncheck a menu item: the change cascades to all descendants, and checking an item
 * also checks its ancestors (a child cannot be visible under a hidden parent).
 * @param {ApiManagerMenuItem[]} items
 * @param {string[]} idPath - Ids from root to the target item
 * @param {boolean} available
 * @returns {ApiManagerMenuItem[]}
 */
export const setMenuItemAvailable = (items, idPath, available) => {
  if (!items?.length || !idPath?.length) return items ?? [];

  const [headId, ...restPath] = idPath;
  return items.map((item) => {
    if (item.id !== headId) return item;
    if (!restPath.length) return setSubtreeAvailable(item, available);

    const nextItem = available ? patchConfig(item, { available: true }) : item;
    return { ...nextItem, items: setMenuItemAvailable(item.items ?? [], restPath, available) };
  });
};

/**
 * Toggle favorite on a leaf. Ignored on items with children or unavailable items.
 * @param {ApiManagerMenuItem[]} items
 * @param {string[]} idPath - Ids from root to the target item
 * @param {boolean} favorite
 * @returns {ApiManagerMenuItem[]}
 */
export const setMenuItemFavorite = (items, idPath, favorite) => {
  if (!items?.length || !idPath?.length) return items ?? [];

  const [headId, ...restPath] = idPath;
  return items.map((item) => {
    if (item.id !== headId) return item;
    if (restPath.length) {
      return { ...item, items: setMenuItemFavorite(item.items ?? [], restPath, favorite) };
    }
    if (hasMenuChildren(item) || !item.config?.available) return item;
    return patchConfig(item, { favorite });
  });
};

/**
 * @param {ApiManagerMenuItem} item
 * @returns {boolean}
 */
const hasUnavailableDescendant = (item) => (item.items ?? []).some(
  (child) => !child.config?.available || hasUnavailableDescendant(child),
);

/**
 * Checkbox state in the management modal: indeterminate when some descendants are hidden.
 * @param {ApiManagerMenuItem} item
 * @returns {MenuItemCheckState}
 */
export const getMenuItemCheckState = (item) => {
  if (!item?.config?.available) return 'unchecked';
  return hasUnavailableDescendant(item) ? 'indeterminate' : 'checked';
};

/**
 * Ids from root to the item with `id` (empty when not found).
 * @param {ApiManagerMenuItem[] | null | undefined} items
 * @param {string | null | undefined} id
 * @returns {string[]}
 */
export const getMenuItemIdPath = (items, id) => {
  if (!items?.length || !id) return [];

  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];
    if (item.id === id) return [item.id];
    const childPath = getMenuItemIdPath(item.items, id);
    if (childPath.length) return [item.id, ...childPath];
  }
  return [];
};

/**
 * @param {ApiManagerMenuItem[] | null | undefined} items
 * @returns {string[]}
 */
const collectMenuItemIds = (items) => (items ?? []).flatMap((item) => [item.id, ...collectMenuItemIds(item.items)]);

/**
 * Ids displayed by a search in the management tree: matching items, their ancestors and their children.
 * @param {ApiManagerMenuItem[] | null | undefined} items - Full menu tree
 * @param {string} search
 * @returns {Set<string> | null} `null` when the search is empty (whole tree displayed)
 */
export const getMenuItemIdsMatchingSearch = (items, search) => {
  if (!search?.trim()) return null;
  return new Set(collectMenuItemIds(filterMenuItemsBySearch(items ?? [], search)));
};

/**
 * Remove an item from favorites by id (favorites list quick action).
 * @param {ApiManagerMenuItem[] | null | undefined} items - Full menu tree
 * @param {string} itemId
 * @returns {ApiManagerMenuItem[]} Same tree when the id is unknown
 */
export const removeMenuFavorite = (items, itemId) => {
  const idPath = getMenuItemIdPath(items, itemId);
  if (!idPath.length) return items ?? [];
  return setMenuItemFavorite(items ?? [], idPath, false);
};

/**
 * Toggle an item favorite by id (menu and popover star quick action).
 * @param {ApiManagerMenuItem[] | null | undefined} items - Full menu tree
 * @param {string} itemId
 * @returns {ApiManagerMenuItem[]} Same tree when the id is unknown
 */
export const toggleMenuFavorite = (items, itemId) => {
  const idPath = getMenuItemIdPath(items, itemId);
  if (!idPath.length) return items ?? [];
  const item = findMenuItemById(items, itemId);
  return setMenuItemFavorite(items ?? [], idPath, !item?.config?.favorite);
};

/**
 * Project menu items to the PartialMenuItem shape for POST /menu.
 * @param {ApiManagerMenuItem[] | null | undefined} items
 * @returns {ApiPartialMenuItem[]}
 */
export const mapMenuItemsToPartialMenuItems = (items) => {
  if (!items?.length) return [];

  return items.map((item) => ({
    id: item.id,
    favorite: !!item.config?.favorite,
    available: !!item.config?.available,
    items: item.items?.length ? mapMenuItemsToPartialMenuItems(item.items) : null,
  }));
};

/**
 * Build POST /menu body from the full local menu tree (same shape as the response menu).
 * @param {ApiManagerMenuItem[]} items
 * @param {ApiManagerMenuItem[] | null | undefined} [bottomSectionItems] - Sent back unchanged
 * @returns {ApiMenuPostRequest}
 */
export const buildMenuPostRequest = (items, bottomSectionItems) => ({
  menu: {
    items: mapMenuItemsToPartialMenuItems(items),
    bottomSection: { items: mapMenuItemsToPartialMenuItems(bottomSectionItems) },
  },
});

/**
 * Menu tree shown in the management modal: forbidden items (`allowed: false`) and their subtree are not configurable.
 * @param {ApiManagerMenuItem[] | null | undefined} items
 * @returns {ApiManagerMenuItem[]}
 */
export const getConfigurableMenuItems = (items) => (items ?? []).reduce((configurable, item) => {
  if (isMenuItemForbidden(item)) return configurable;
  configurable.push({ ...item, items: item.items ? getConfigurableMenuItems(item.items) : item.items });
  return configurable;
}, /** @type {ApiManagerMenuItem[]} */ ([]));
